import { callLambdaFunction } from '../aiClient';
import { supabase } from '../../lib/supabase';

const CHAT_COMPLETION_ENDPOINT = import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;

function isLambdaConfigured() {
  const endpoint = CHAT_COMPLETION_ENDPOINT;
  return (
    endpoint &&
    !endpoint?.includes('your-lambda') &&
    !endpoint?.includes('your-')
  );
}

async function callOpenAIViaSupabase(messages, options = {}) {
  const { max_completion_tokens, max_tokens, model: _model, ...rest } = options;

  const result = await supabase?.functions?.invoke('generate-blueprint', {
    body: {
      messages,
      model: 'gpt-4o',
      max_completion_tokens: max_completion_tokens || max_tokens || 3000,
    },
  });

  if (result?.error) {
    let errMsg = result?.error?.message || 'AI service is currently unavailable. Please try again.';
    try {
      const ctx = result?.error?.context;
      if (ctx) {
        const body = await ctx?.json?.();
        if (body?.error) errMsg = body?.error;
      }
    } catch (_) {}
    throw new Error(errMsg);
  }

  const data = result?.data;
  if (data?.error) {
    throw new Error(data.error);
  }

  const content = data?.choices?.[0]?.message?.content;
  if (!content) {
    throw new Error('AI service returned an empty response. Please try again.');
  }

  return content;
}

export async function getChatCompletion(provider, model, messages, options = {}) {
  if (isLambdaConfigured()) {
    const { stream, ...parameters } = options;
    const payload = {
      provider,
      model,
      messages,
      stream: false,
      parameters,
    };
    const response = await callLambdaFunction(CHAT_COMPLETION_ENDPOINT, payload);
    return response?.choices?.[0]?.message?.content ?? response;
  }

  // Fallback: call OpenAI via Supabase Edge Function (avoids browser CORS issues)
  return callOpenAIViaSupabase(messages, options);
}

export async function getStreamingChatCompletion(
  provider,
  model,
  messages,
  onChunk,
  onComplete,
  onError,
  options = {}
) {
  if (!isLambdaConfigured()) {
    // Fallback: non-streaming call via Supabase Edge Function
    try {
      const content = await callOpenAIViaSupabase(messages, options);
      onChunk?.({ choices: [{ delta: { content } }] });
      onComplete?.(content, []);
    } catch (err) {
      onError?.(err);
    }
    return;
  }

  const { stream, ...parameters } = options;
  const payload = {
    provider,
    model,
    messages,
    stream: true,
    parameters,
  };

  try {
    const response = await fetch(CHAT_COMPLETION_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const reader = response?.body?.getReader();
    const decoder = new TextDecoder();
    let fullContent = '';
    const chunks = [];

    while (true) {
      const { done, value } = await reader?.read();
      if (done) break;

      const text = decoder?.decode(value);
      const lines = text?.split('\n')?.filter((l) => l?.startsWith('data: '));

      for (const line of lines) {
        try {
          const data = JSON.parse(line?.slice(6));
          if (data?.type === 'chunk' && data?.chunk) {
            chunks?.push(data?.chunk);
            const content = data?.chunk?.choices?.[0]?.delta?.content || '';
            fullContent += content;
            onChunk?.(data?.chunk);
          } else if (data?.type === 'done') {
            onComplete?.(fullContent, chunks);
          } else if (data?.type === 'error') {
            onError?.(new Error(data.error));
          }
        } catch (_) {}
      }
    }
  } catch (err) {
    console.error('Lambda Function Error:', { error: err, details: err?.message });
    onError?.(err);
  }
}
